import amazonAds from './amazonAds';
import * as ConsentManager from './consent';
import googleTags from './google';
import {
  configureLDUStates,
  getConfiguredStates,
  getLDUOptions,
  isAffectedState,
  shouldApplyLDU
} from './limitedDataUse';
import metaPixel from './metaPixel';
import tddPixel from './tdd';
import type { TrackingOptions } from './types';
import { BrandclubEventType, SimpleTrackingProvider, TrackingPixel } from './types';
import { parseEventAndPayload } from './utils';

// Special key for enabling debug mode via params or localStorage
const DEBUG_KEY = 'bc-debug';
const DEBUG_STORAGE_KEY = 'bc-debug';
const OVERRIDE_CONSENT_KEY = 'bc_tracking_consent';

let debug = false;

const log = (...args: any[]) => {
  if (!debug) return;
  // eslint-disable-next-line no-console
  console.info(...['[externalTracking]'].concat(args));
};

// Registry of tracking providers
const trackingProviders: Record<string, SimpleTrackingProvider> = {
  Meta: {
    enableDebug: () => metaPixel.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions, externalTracking }) =>
      metaPixel.track({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions, externalTracking }),
    grantConsent: () => metaPixel.grantConsent(),
    revokeConsent: () => metaPixel.revokeConsent()
  },
  GoogleAds: {
    enableDebug: () => googleTags.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      googleTags.track({ brandId, pixels, eventName, eventType, eventPayload }),
    grantConsent: () => googleTags.grantConsent(),
    revokeConsent: () => googleTags.revokeConsent()
  },
  // Key MUST equal the stored `pixelProvider` value — `track` dispatches via
  // `trackingProviders[pixel.pixelProvider]`. Every config surface writes
  // `'TheTradeDesk'` (the `PixelProvider` enum, both FE and backend), so the old
  // `TTD` key resolved to undefined and EVERY Trade Desk event was dropped with
  // "Provider not registered" — on Pages and in the checkout app alike.
  TheTradeDesk: {
    enableDebug: () => tddPixel.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      tddPixel.track({ brandId, pixels, eventName, eventType, eventPayload }),
    grantConsent: () => tddPixel.grantConsent(),
    revokeConsent: () => tddPixel.revokeConsent()
  },
  AmazonAds: {
    enableDebug: () => amazonAds.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      amazonAds.track({ brandId, pixels, eventName, eventType, eventPayload }),
    grantConsent: () => amazonAds.grantConsent(),
    revokeConsent: () => amazonAds.revokeConsent()
  }

};

// Register all providers with ConsentManager
Object.values(trackingProviders).forEach(provider => {
  ConsentManager.registerProvider(provider);
});

/**
 * Register a new tracking provider
 */
export function registerTrackingProvider(
  providerId: string,
  provider: SimpleTrackingProvider
): void {
  trackingProviders[providerId] = provider;
  ConsentManager.registerProvider(provider);
  log(`Registered tracking provider: ${providerId}`);
}

/**
 * Enable debug mode for all providers and consent manager
 */
export function enableDebug(): void {
  if (debug) return;
  debug = true;

  // Persist debug mode in localStorage
  try {
    localStorage?.setItem(DEBUG_STORAGE_KEY, '1');
  } catch (e) {
    // Ignore localStorage errors
  }

  log('Debug mode enabled');
  Object.values(trackingProviders).forEach(provider => provider.enableDebug());
  ConsentManager.enableDebug();

  // Expose debug helpers on window for console testing
  if (typeof window !== 'undefined') {
    (window as any).__bcDebug = {
      // LDU testing helpers
      ldu: {
        getConfiguredStates,
        isAffectedState: (state: string) => isAffectedState(state),
        shouldApplyLDU: (shipping?: string, billing?: string) => shouldApplyLDU(shipping, billing),
        getLDUOptions: (shipping?: string, billing?: string) => getLDUOptions(shipping, billing),
        configureStates: (states: string[]) => configureLDUStates(states),
      },
      // Quick test helper for LDU
      testLDU: (shippingState?: string, billingState?: string) => {
        const result = {
          configuredStates: getConfiguredStates(),
          shippingState,
          billingState,
          shippingAffected: isAffectedState(shippingState),
          billingAffected: isAffectedState(billingState),
          shouldApply: shouldApplyLDU(shippingState, billingState),
          options: getLDUOptions(shippingState, billingState),
        };
        // eslint-disable-next-line no-console
        console.table(result);
        return result;
      },
      // Dry-run mode: logs checkout & analytics but doesn't place orders or send pixels
      // Auto-expires after 10 minutes by default
      dryRun: {
        enable: (minutes = 10) => {
          const expiresAt = Date.now() + minutes * 60 * 1000;
          localStorage.setItem('bc-do-not-place-order', String(expiresAt));
          // eslint-disable-next-line no-console
          console.log(`🧪 DRY RUN mode ENABLED - expires in ${minutes} minutes (${new Date(expiresAt).toLocaleTimeString()})`);
          // eslint-disable-next-line no-console
          console.log('   • Checkout: orders will NOT be placed');
          // eslint-disable-next-line no-console
          console.log('   • Analytics: events will be logged but NOT sent to pixels');
        },
        disable: () => {
          localStorage.removeItem('bc-do-not-place-order');
          // eslint-disable-next-line no-console
          console.log('✅ DRY RUN mode DISABLED - orders will be placed, analytics will be sent');
        },
        isEnabled: () => {
          const expiresAt = localStorage.getItem('bc-do-not-place-order');
          if (!expiresAt) return false;
          const isValid = Date.now() < Number(expiresAt);
          if (!isValid) {
            localStorage.removeItem('bc-do-not-place-order');
          }
          return isValid;
        },
        getTimeRemaining: () => {
          const expiresAt = localStorage.getItem('bc-do-not-place-order');
          if (!expiresAt) return null;
          const remaining = Number(expiresAt) - Date.now();
          if (remaining <= 0) {
            localStorage.removeItem('bc-do-not-place-order');
            return null;
          }
          return Math.ceil(remaining / 60000); // minutes remaining
        },
      },
    };
    log('Debug helpers available at window.__bcDebug');
    log('Try: __bcDebug.testLDU("RI") or __bcDebug.dryRun.enable()');
  }
}

/**
 * Disable debug mode for all providers
 */
export function disableDebug(): void {
  debug = false;

  try {
    localStorage?.removeItem(DEBUG_STORAGE_KEY);
  } catch (e) {
    // Ignore localStorage errors
  }

  // eslint-disable-next-line no-console
  console.info('[externalTracking] Debug mode disabled');
}

/**
 * Set user identity for Meta Pixel advanced matching
 */
export function setIdentity(identity: string): void {
  metaPixel.setIdentity(identity);
}

/**
 * Grant consent for all tracking providers
 */
export function grantConsent(): void {
  ConsentManager.setConsent(true);
}

/**
 * Revoke consent for all tracking providers
 */
export function revokeConsent(): void {
  ConsentManager.setConsent(false);
}

/**
 * Check if user has granted tracking consent
 */
export function hasConsent(): boolean {
  const overrideConsent = localStorage?.getItem(OVERRIDE_CONSENT_KEY);
  if (overrideConsent) {
    return overrideConsent === 'true';
  }
  return ConsentManager.hasConsent();
}

/**
 * Extract pixel container from checkoutItems or cartItems if not provided
 * Fallback for single-item cart events
 */
function extractPixelContainerFromCheckoutItems(
  params: { [key: string]: any },
  pixelContainer: { brandId: string; pixels: TrackingPixel[] }
): { brandId: string; pixels: TrackingPixel[] } {
  // If pixelContainer is valid, return as-is
  if (pixelContainer?.brandId && pixelContainer?.pixels?.length) {
    return pixelContainer;
  }

  // Try to extract from first checkout item or cart item
  const firstItem = params?.checkoutItems?.[0] || params?.cartItems?.[0];
  if (firstItem?.pixels && firstItem?.brandId) {
    log('Extracted pixel container from first checkout/cart item');
    return {
      brandId: String(firstItem.brandId),
      pixels: firstItem.pixels,
    };
  }

  return pixelContainer;
}

/**
 * Enrich params with first item's metadata if available
 */
function enrichParamsFromCheckoutItems(params: { [key: string]: any }): { [key: string]: any } {
  const firstItem = params?.checkoutItems?.[0] || params?.cartItems?.[0];
  if (!firstItem) {
    return params;
  }

  // Only add missing fields from first item
  return {
    brandId: firstItem.brandId,
    brandName: firstItem.brandName,
    categoryId: firstItem.categoryId,
    categoryName: firstItem.categoryName,
    subCategoryId: firstItem.subCategoryId,
    subCategoryName: firstItem.subCategoryName,
    retailerId: firstItem.retailerId,
    retailerSku: firstItem.retailerSku,
    stacklineSku: firstItem.stacklineSku,
    ...params, // Original params override defaults
  };
}

/**
 * Validate that event has required parameters for tracking
 */
function isValidTrackingEvent(
  eventName: string | undefined,
  eventPayload: any
): boolean {
  if (!eventName) {
    return false;
  }
  // Must have at least one identifier: brandId, SKU, or checkoutItems or cartItems
  return !!(
    eventPayload?.brandId ||
    eventPayload?.stacklineSku ||
    (eventPayload?.checkoutItems?.length > 0) ||
    (eventPayload?.cartItems?.length > 0)
  );
}

/**
 * Check if debug mode should be enabled via params or localStorage
 */
function checkAndEnableDebugMode(params: { [key: string]: any }): void {
  // Check if debug key is in params (takes priority)
  if (params?.[DEBUG_KEY]) {
    enableDebug();
    // Clean up the debug key from params
    delete params[DEBUG_KEY];
    return;
  }

  // Check localStorage for persisted debug mode
  if (!debug) {
    const debugStored = localStorage?.getItem(DEBUG_STORAGE_KEY);
    if (debugStored === '1') {
      enableDebug();
    }
  }
}

/**
 * Track an event with all registered providers
 */
export function track(
  event: string,
  params: { [key: string]: any },
  pixelContainer: { brandId: string; pixels: TrackingPixel[] }
): void {
  // Handle debug mode activation
  checkAndEnableDebugMode(params);

  log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  log(`Tracking event: ${event}`, 'Params:', params, 'Pixel Container:', pixelContainer);

  // Extract or enrich pixel container from checkout items if needed
  const enrichedPixelContainer = extractPixelContainerFromCheckoutItems(params, pixelContainer);
  let enrichedParams = params;

  // If single checkout item or cart item, enrich params with item metadata
  if (params?.checkoutItems?.length === 1 || params?.cartItems?.length === 1) {
    enrichedParams = enrichParamsFromCheckoutItems(params);
    log('Enriched params from first checkout/cart item');
  }

  // Validate required parameters
  if (!enrichedParams?.v_id) {
    log('❌ Missing required visitor ID (v_id)');
    return;
  }

  if (!enrichedPixelContainer?.brandId || !enrichedPixelContainer?.pixels?.length) {
    log('❌ Missing brand ID or pixels configuration');
    return;
  }

  // Check consent before tracking
  if (!ConsentManager.hasConsent()) {
    log('❌ Tracking blocked: user consent not granted');
    return;
  }

  // Group pixels by provider
  const pixelsByProvider = enrichedPixelContainer.pixels.reduce(
    (acc: Record<string, TrackingPixel[]>, pixel) => {
      acc[pixel.pixelProvider] = [...(acc[pixel.pixelProvider] || []), pixel];
      return acc;
    },
    {}
  );
  // Build tracking options for providers
  // Currently supports LDU (Meta Limited Data Use) - extensible for future controls
  const shippingState = enrichedParams?.shippingState;
  const billingState = enrichedParams?.billingState;
  const lduOptions = getLDUOptions(shippingState, billingState);

  const trackingOptions: TrackingOptions | null = lduOptions ? { ldu: lduOptions } : null;

  if (trackingOptions?.ldu?.enabled) {
    log(`LDU will be applied - shippingState: ${shippingState}, billingState: ${billingState}`);
  }

  // Check if dry-run mode is enabled (same flag as checkout dry-run)
  const dryRunExpiresAt = localStorage?.getItem('bc-do-not-place-order');
  const isDryRun = dryRunExpiresAt && Date.now() < Number(dryRunExpiresAt);

  // Track with each provider
  Object.entries(pixelsByProvider).forEach(([providerName, providerPixels]) => {
    const provider = trackingProviders[providerName];
    if (!provider) {
      log(`Provider not registered: ${providerName}`);
      return;
    }
    try {
      providerPixels.forEach((pixel) => {
        const { eventName, eventType, eventPayload } = parseEventAndPayload(
          event,
          enrichedParams,
          pixel.eventMapping
        );

        if (!isValidTrackingEvent(eventName, eventPayload) || !eventName) {
          log('Event not supported or missing identifiers:', event);
          return;
        }
        log('✅ Consent granted, event is valid, proceeding with tracking');

        // DRY RUN MODE: Log what would be sent but don't actually track
        if (isDryRun) {
          // eslint-disable-next-line no-console
          console.log(`🧪 [DRY RUN] Would track to ${providerName}:`, {
            eventName,
            eventType,
            eventPayload,
            pixelId: pixel.pixelId,
            trackingOptions,
          });
          return;
        }

        provider.track({
          brandId: enrichedPixelContainer.brandId,
          pixels: [pixel],
          eventName,
          eventType: eventType as BrandclubEventType,
          eventPayload,
          // Pass tracking options to providers (each extracts what it needs)
          trackingOptions,
          // Pass external tracking reports (each provider extracts its own data)
          externalTracking: enrichedParams?.externalTracking,
        });

        log(`Tracked with ${providerName}:`, eventName);
      });
    } catch (error) {
      log(`Error tracking with ${providerName}:`, error);
    }
  });
} 
